import apiClient from './apiClient';

class ReportService {
  /**
   * Generate AI strategic insights for one department group.
   * Sends a token-optimised payload: dept name, KPIs, and worst-performing
   * questions only (text + success %) — no IDs, no raw counts.
   */
  /**
   * Export the multi-campaign report as a PDF via jsreport.
   * Triggers a file download in the browser.
   */
  async exportPdf({ reportName, aggregates, aiInsights, reportLabel, networkName, orgLabel, orgLabelPlural, clientLogoUrl }) {
    try {
      const response = await apiClient.post(
        '/reports/export-pdf',
        { reportName, aggregates, aiInsights, reportLabel, networkName, orgLabel, orgLabelPlural, clientLogoUrl },
        { responseType: 'blob', timeout: 120000 }
      );
      const url      = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link     = document.createElement('a');
      const safeName = (reportName || 'report').replace(/[^a-zA-Z0-9-_\s]/g, '').trim().replace(/\s+/g, '-') || 'report';
      const now1     = new Date();
      const ts1      = `${now1.getFullYear()}-${String(now1.getMonth()+1).padStart(2,'0')}-${String(now1.getDate()).padStart(2,'0')}_${String(now1.getHours()).padStart(2,'0')}-${String(now1.getMinutes()).padStart(2,'0')}`;
      link.href      = url;
      link.download  = `${safeName}_${ts1}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      console.error('Export PDF error:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to export PDF' };
    }
  }

  async exportBuPdf({ reportName, orgLabel = 'Dealership', reportLabel = 'Dealership Results Dashboard', reportSubtitle = 'Performance Report', campaignName = '', organization, period, overview, leaderboard, departments, comparison, allParticipants = [], nonStarters = [], scoreBuckets = {}, campaignBreakdown = [], campaignDetails = [], clientLogoUrl = '', aiInsight = null, template = 'default' }) {
    try {
      const response = await apiClient.post(
        '/reports/export-bu-pdf',
        { reportName, orgLabel, reportLabel, reportSubtitle, campaignName, organization, period, overview, leaderboard, departments, comparison, allParticipants, nonStarters, scoreBuckets, campaignBreakdown, campaignDetails, clientLogoUrl, aiInsight, template },
        { responseType: 'blob', timeout: 120000 }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      const safeName = (reportName || 'bu-report').replace(/[^a-zA-Z0-9-_\s]/g, '').trim().replace(/\s+/g, '-') || 'bu-report';
      const now2 = new Date();
      const ts2  = `${now2.getFullYear()}-${String(now2.getMonth()+1).padStart(2,'0')}-${String(now2.getDate()).padStart(2,'0')}_${String(now2.getHours()).padStart(2,'0')}-${String(now2.getMinutes()).padStart(2,'0')}`;
      link.href = url;
      link.download = `${safeName}_${ts2}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to export PDF' };
    }
  }

  async getDeptAIInsights({ departmentName, completionRate, avgScore, staffCount, knowledgeGaps, questions }) {
    try {
      const response = await apiClient.post('/reports/dept-ai-insights', {
        departmentName,
        completionRate: Math.round(completionRate || 0),
        avgScore: Math.round(avgScore || 0),
        staffCount: staffCount || 0,
        knowledgeGaps: knowledgeGaps || 0,
        questions: (questions || []).slice(0, 10).map(q => ({ q: q.q, sc: Math.round(q.sc || 0) })),
      }, { timeout: 180000 });
      return { success: true, data: response.data?.data || {} };
    } catch (error) {
      console.error('Dept AI insights error:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to generate AI insights' };
    }
  }

  async getNetworkInsights({ networkName, period, groups }) {
    try {
      const response = await apiClient.post('/reports/network-insights', { networkName, period, groups }, { timeout: 180000 });
      return { success: true, data: response.data?.data || {} };
    } catch (error) {
      console.error('Network insights error:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to generate network insights' };
    }
  }

  async getBuAIInsights({ orgName, period, completionRate, avgScore, benchmark, networkAvgCr, networkAvgScore, totalStaff, nonStartersCount, scoreBuckets, campaignBreakdown, departments, topPerformers, timingStats, priorMonths, nonQualifierNames, lowestScorers, campaignQuestions }) {
    try {
      const response = await apiClient.post('/reports/bu-ai-insights', {
        orgName, period, completionRate, avgScore, benchmark,
        networkAvgCr, networkAvgScore, totalStaff, nonStartersCount,
        scoreBuckets, campaignBreakdown, departments, topPerformers, timingStats,
        priorMonths, nonQualifierNames, lowestScorers, campaignQuestions,
      }, { timeout: 180000 });
      return { success: true, data: response.data?.data || {} };
    } catch (error) {
      console.error('BU AI insights error:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to generate AI insights' };
    }
  }

  // ── Saved Reports ────────────────────────────────────────────────────────────

  async getSavedReports() {
    try {
      const response = await apiClient.get('/reports/saved');
      return { success: true, data: response.data?.data || [] };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to load saved reports', data: [] };
    }
  }

  async getSavedReport(id) {
    try {
      const response = await apiClient.get(`/reports/saved/${id}`);
      return { success: true, data: response.data?.data || null };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to load report' };
    }
  }

  async saveReport({ title, managingGroup, reportMonth, reportType, config, reportData, aiInsights }) {
    try {
      const response = await apiClient.post('/reports/saved', { title, managingGroup, reportMonth, reportType, config, reportData, aiInsights });
      return { success: true, data: response.data?.data || {} };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to save report' };
    }
  }

  async updateSavedReport(id, updates) {
    try {
      const response = await apiClient.patch(`/reports/saved/${id}`, updates);
      return { success: true, data: response.data?.data || {} };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update report' };
    }
  }

  async deleteSavedReport(id) {
    try {
      await apiClient.delete(`/reports/saved/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete report' };
    }
  }
}

export default new ReportService();
